import { Component, inject, OnInit, signal, computed } from '@angular/core';
import { TableModule } from 'primeng/table';
import { PageLayoutComponent } from '../../shared/page-layout/page-layout';
import { DatePipe, NgClass } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormGroup, Validators, FormBuilder, AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { UserService } from '../../../service/user.service';
import { User, UserResponse } from '../../models/user.model';
import { InputText } from 'primeng/inputtext';
import { Paginator } from 'primeng/paginator';
import { Button } from 'primeng/button';
import { Tooltip } from 'primeng/tooltip';
import { Router, RouterLink } from '@angular/router';
import { forkJoin } from 'rxjs';
import { Dialog } from 'primeng/dialog';
import { ToastModule } from 'primeng/toast';
import { Select } from 'primeng/select';
import { ConfirmationService, MessageService } from 'primeng/api';
import { IconField } from 'primeng/iconfield';
import { InputIcon } from 'primeng/inputicon';
import { ConfirmDialog } from 'primeng/confirmdialog';
import { finalize } from 'rxjs/operators';
import { StateContainerComponent, ViewState } from '../../shared/state-container/state-container.component';

// Custom validator for password matching
export const passwordMatchValidator: ValidatorFn = (
  control: AbstractControl
): ValidationErrors | null => {
  const newPassword = control.get('newPassword');
  const confirmPassword = control.get('confirmPassword');

  if (newPassword && confirmPassword && newPassword.value !== confirmPassword.value) {
    return { passwordMismatch: true };
  }
  return null;
};

@Component({
  selector: 'app-user',
  imports: [
    TableModule,
    PageLayoutComponent,
    NgClass,
    FormsModule,
    DatePipe,
    InputText,
    Paginator,
    Button,
    Tooltip,
    RouterLink,
    Dialog,
    ReactiveFormsModule,
    ToastModule,
    Select,
    IconField,
    InputIcon,
    ConfirmDialog,
    StateContainerComponent
  ],
  templateUrl: './user-list.html',
  styleUrl: './user-list.css',
  providers: [MessageService, ConfirmationService],
})
export class UserList implements OnInit {
  users = signal<User[]>([]);
  totalRecords = signal(0);
  loading = signal(false);
  error = signal<any>(null);

  rows = 10;
  searchTerm = '';
  selectedUsers = signal<User[]>([]);

  sortField: string = '';
  sortOrder: number = 0;

  activeFilter: boolean | null = null;

  addUserForm!: FormGroup;
  showAddUserDialog = false;
  
  user = signal<User | null>(null);
  isEditMode = signal(false);
  editUserId = signal<string | null>(null);
  
  // Change Password properties
  showChangePasswordDialog = false;
  changingPassword = signal(false);
  changePasswordForm!: FormGroup;
  selectedUserForPasswordChange = signal<User | null>(null);
  
  private messageService = inject(MessageService);
  private confirmationService = inject(ConfirmationService);

  statusOptions = [
    { label: 'All Users', value: null },
    { label: 'Active Users', value: true },
    { label: 'Inactive Users', value: false }
  ];

  // Computed state for StateContainer
  readonly viewState = computed<ViewState>(() => {
    if (this.loading()) return 'loading';
    if (this.error()) return 'error';
    if (this.users().length === 0) return 'empty';
    return 'content';
  });

  readonly showClearFilters = computed(() => {
    return !!(this.searchTerm || this.activeFilter !== null);
  });

  readonly emptyStateMessage = computed(() => {
    if (this.searchTerm || this.activeFilter !== null) {
      return 'No users match your current search or filter criteria. Try adjusting your filters.';
    }
    return 'Create your first user to start managing your users.';
  });

  readonly errorMessage = computed(() => {
    const err = this.error();
    if (typeof err === 'string') return err;
    if (err?.message) return err.message;
    return 'Failed to load users. Please try again.';
  });

  constructor(
    private fb: FormBuilder,
    private userService: UserService,
    private router: Router
  ) {}

  ngOnInit() {
    this.loadUsers(0);
    this.initForm();
    this.initChangePasswordForm();
    const key = 'user-table';
    const state = localStorage.getItem(key);

    if (state) {
      const parsed = JSON.parse(state);
      delete parsed.selection;
      delete parsed.selectionKeys;
      localStorage.setItem(key, JSON.stringify(parsed));
    }
    this.selectedUsers.set([]);
  }

  initForm() {
    this.addUserForm = this.fb.group({
      name: ['', Validators.required],
      username: ['', Validators.required],
      email: ['', [Validators.required, Validators.email]],
      mobile: ['', [Validators.required, Validators.pattern(/^[0-9]{10}$/)]],
      password: ['', [Validators.required, Validators.minLength(6)]],
      enableLogin: [true]
    });
  }

  initChangePasswordForm() {
    this.changePasswordForm = this.fb.group(
      {
        newPassword: ['', [Validators.required, Validators.minLength(6)]],
        confirmPassword: ['', [Validators.required]]
      },
      { validators: passwordMatchValidator }
    );
  }

  loadUsers(page = 0) {
    this.loading.set(true);
    this.error.set(null);

    const sort =
      this.sortField && this.sortOrder !== 0
        ? `${this.sortField},${this.sortOrder === 1 ? 'asc' : 'desc'}`
        : '';

    this.userService.getUsers(page, this.rows, this.searchTerm, sort, this.activeFilter)
      .subscribe({
        next: (res: UserResponse) => {
          this.users.set(res.content);
          this.totalRecords.set(res.page.totalElements);
          this.loading.set(false);
        },
        error: (err) => {
          this.error.set(err);
          this.loading.set(false);
        }
      });
  }

  // Retry method for error state
  retryLoad() {
    this.loadUsers(0);
  }

  clearFilters() {
    this.searchTerm = '';
    this.activeFilter = null;
    this.sortField = '';
    this.sortOrder = 0;
    this.loadUsers(0);
  }

  editSelected() {
    const selectedUsers = this.selectedUsers();
    
    if (selectedUsers.length !== 1) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Warning',
        detail: 'Please select exactly one user to edit'
      });
      return;
    }

    const userToEdit = selectedUsers[0];
    this.openEditUserDialog(String(userToEdit.id));
  }

  openEditUserDialog(userId: string) {
    this.loading.set(true);
    this.editUserId.set(userId);
    this.isEditMode.set(true);
    
    this.userService.getUserById(userId)
      .pipe(finalize(() => this.loading.set(false)))
      .subscribe({
        next: (user) => {
          this.user.set(user);
          this.patchFormForEdit(user);
          this.showAddUserDialog = true;
        },
        error: (err) => {
          console.error('Error fetching user:', err);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to load user details for editing'
          });
          this.isEditMode.set(false);
          this.editUserId.set(null);
        }
      });
  }

  patchFormForEdit(user: User) {
    this.addUserForm.patchValue({
      name: user.name,
      username: user.username,
      email: user.email,
      mobile: user.mobile || '',
      password: '',
      enableLogin: user.enabled
    });
    
    this.addUserForm.get('password')?.clearValidators();
    this.addUserForm.get('password')?.updateValueAndValidity();
    
    this.addUserForm.markAsPristine();
  }

  createUser() {
    if (this.addUserForm.invalid) {
      this.addUserForm.markAllAsTouched();
      return;
    }

    const formValue = this.addUserForm.value;
    
    if (this.isEditMode() && this.editUserId()) {
      const updateData: any = {
        name: formValue.name,
        username: formValue.username,
        email: formValue.email,
        mobile: formValue.mobile,
        enabled: formValue.enableLogin
      };
      
      if (formValue.password && formValue.password.trim().length > 0) {
        updateData.password = formValue.password;
      }
      
      this.userService.updateUser(this.editUserId()!, updateData).subscribe({
        next: () => {
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: 'User updated successfully'
          });
          this.closeDialogAndRefresh();
        },
        error: (err) => {
          console.error('Error updating user:', err);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: err.error?.message || 'Failed to update user'
          });
        }
      });
    } else {
      this.userService.createUser(formValue).subscribe({
        next: () => {
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: 'User created successfully'
          });
          this.closeDialogAndRefresh();
        },
        error: (err) => {
          console.error('Error creating user:', err);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: err.error?.message || 'Failed to create user'
          });
        }
      });
    }
  }

  closeDialog() {
    this.showAddUserDialog = false;
    this.isEditMode.set(false);
    this.editUserId.set(null);
    this.user.set(null);
    this.addUserForm.get('password')?.setValidators([Validators.required, Validators.minLength(6)]);
    this.addUserForm.get('password')?.updateValueAndValidity();
    this.addUserForm.reset({
      name: '',
      username: '',
      email: '',
      mobile: '',
      password: '',
      enableLogin: true
    });
  }

  private closeDialogAndRefresh() {
    this.showAddUserDialog = false;
    this.isEditMode.set(false);
    this.editUserId.set(null);
    this.user.set(null);
    this.addUserForm.get('password')?.setValidators([Validators.required, Validators.minLength(6)]);
    this.addUserForm.get('password')?.updateValueAndValidity();
    this.loadUsers(0);
  }

  protected openAddUserDialog() {
    this.isEditMode.set(false);
    this.editUserId.set(null);
    this.user.set(null);
    
    this.addUserForm.get('password')?.setValidators([Validators.required, Validators.minLength(6)]);
    this.addUserForm.get('password')?.updateValueAndValidity();
    
    this.addUserForm.reset({
      name: '',
      username: '',
      email: '',
      mobile: '',
      password: '',
      enableLogin: true
    });
    
    this.showAddUserDialog = true;
  }

  // Change Password Methods
  openChangePasswordDialog(user: User) {
    this.selectedUserForPasswordChange.set(user);
    this.changePasswordForm.reset({
      newPassword: '',
      confirmPassword: ''
    });
    this.showChangePasswordDialog = true;
  }

  closeChangePasswordDialog() {
    this.showChangePasswordDialog = false;
    this.selectedUserForPasswordChange.set(null);
    this.changePasswordForm.reset();
  }

  changePassword() {
    if (this.changePasswordForm.invalid) {
      this.changePasswordForm.markAllAsTouched();
      return;
    }

    const user = this.selectedUserForPasswordChange();
    if (!user) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'No user selected'
      });
      return;
    }

    this.changingPassword.set(true);
    const newPassword = this.changePasswordForm.get('newPassword')?.value;

    this.userService.changePassword(user.id, newPassword)
      .pipe(finalize(() => this.changingPassword.set(false)))
      .subscribe({
        next: (response) => {
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: `Password changed successfully for ${user.username}`
          });
          this.closeChangePasswordDialog();
        },
        error: (err) => {
          console.error('Error changing password:', err);
          let errorMessage = 'Failed to change password';
          
          if (err.error?.message) {
            errorMessage = err.error.message;
          } else if (err.status === 403) {
            errorMessage = 'You do not have permission to change this user\'s password';
          } else if (err.status === 404) {
            errorMessage = 'User not found';
          }
          
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: errorMessage
          });
        }
      });
  }

  onSearch() {
    this.sortField = '';
    this.sortOrder = 0;
    this.loadUsers(0);
  }

  onPageChange(event: any) {
    const page = event.page;
    this.rows = event.rows;
    this.loadUsers(page);
  }

  enableSelected() {
    const calls = this.selectedUsers().map(user =>
      this.userService.enableUser(user.id)
    );

    forkJoin(calls).subscribe({
      next: () => {
        this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Users enabled successfully' });
        this.loadUsers();
        this.selectedUsers.set([]);
      },
      error: (err) => {
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to enable users' });
        console.error(err);
      }
    });
  }

  disableSelected() {
    const calls = this.selectedUsers().map(user =>
      this.userService.disableUser(user.id)
    );
    forkJoin(calls).subscribe({
      next: () => {
        this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Users disabled successfully' });
        this.loadUsers();
        this.selectedUsers.set([]);
      },
      error: (err) => {
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to disable users' });
        console.error(err);
      }
    });
  }

  deleteSelected() {
    const selectedUsers = this.selectedUsers();
    const userCount = selectedUsers.length;

    if (!userCount) {
      return;
    }

    this.confirmationService.confirm({
      header: 'Confirm Delete',
      message: `Are you sure you want to delete ${userCount === 1 ? 'this user' : `these ${userCount} users`}?`,
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        forkJoin(selectedUsers.map(user => this.userService.deleteUser(user.id))).subscribe({
          next: () => {
            this.messageService.add({
              severity: 'success',
              summary: 'Success',
              detail: userCount === 1 ? 'User deleted successfully' : 'Users deleted successfully'
            });
            this.selectedUsers.set([]);
            this.loadUsers(0);
          },
          error: (err) => {
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: 'Failed to delete selected users'
            });
            console.error(err);
          }
        });
      }
    });
  }

  protected refreshSelection() {
    this.selectedUsers.set([]);
  }

  onSort(event: any) {
    if (this.loading()) {
      return;
    }

    const field = event.field;

    let newField = this.sortField;
    let newOrder = this.sortOrder;

    if (this.sortField !== field) {
      newField = field;
      newOrder = 1;
    } else {
      if (this.sortOrder === 1) {
        newOrder = -1;
      } else if (this.sortOrder === -1) {
        newOrder = 0;
        newField = '';
      } else {
        newField = field;
        newOrder = 1;
      }
    }

    if (this.sortField === newField && this.sortOrder === newOrder) {
      return;
    }

    this.sortField = newField;
    this.sortOrder = newOrder;
    this.loadUsers(0);
  }

  setActiveFilter(value: any) {
    if (value === true) {
      this.activeFilter = true;
    } else if (value === false) {
      this.activeFilter = false;
    } else {
      this.activeFilter = null;
    }
    this.loadUsers(0);
  }
}